import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { UserChatMessage } from "../../../models/userChatMessage";
import { UserConversation } from "../../../models/userConversation";
import { AxiosHttpClient } from "../../../services/AxiosHttpService";

interface ChatContextType {
  conversations: UserConversation[];
  openChats: string[];
  minimizedChats: string[];
  getConversation: (
    usersKeyOrCertificationId: string,
    setConversation: (c: UserConversation | null) => void
  ) => void;
  openChat: (id: string) => void;
  minimizeChat: (id: string) => void;
  closeChat: (id: string) => void;
  markAsRead: (id: string) => void;
  sendMessage: (conversationId: string, text: string) => void;
  conversationCache: Record<string, UserConversation | null>;
  messageCache: Record<
    string,
    { messages: UserChatMessage[]; hasMore: boolean; page: number }
  >;
  fetchMessages: (
    usersKeyOrCertificationId: string,
    page?: number,
    force?: boolean
  ) => Promise<void>;
  setConversationCache: React.Dispatch<
    React.SetStateAction<Record<string, UserConversation | null>>
  >;
  setMessageCache: React.Dispatch<
    React.SetStateAction<
      Record<
        string,
        { messages: UserChatMessage[]; hasMore: boolean; page: number }
      >
    >
  >;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export function ChatProvider({ children }: { children: ReactNode }) {
  const [conversations, setConversations] = useState<UserConversation[]>([]);
  const [openChats, setOpenChats] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem("openChats");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [minimizedChats, setMinimizedChats] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem("minimizedChats");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [page, setPage] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const limit = 10;
  const [conversationCache, setConversationCache] = useState<
    Record<string, UserConversation | null>
  >({});
  const [messageCache, setMessageCache] = useState<
    Record<
      string,
      { messages: UserChatMessage[]; hasMore: boolean; page: number }
    >
  >({});

  const fetchConversations = () => {
    AxiosHttpClient.get<any>("api/v1/docuveritas/user-conversations/page", {
      pageNumber: page - 1,
      pageSize: limit,
      sort: "lastMessageAt",
      order: "desc",
    })
      .then((res) => {
        setConversations(res.content);
        setTotalElements(res.totalElements);
      })
      .catch((err) => {
        console.error("Failed to fetch conversations:", err);
      });
  };

  useEffect(() => {
    fetchConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  useEffect(() => {
    localStorage.setItem("openChats", JSON.stringify(openChats));
    console.log("useEffect openChats", openChats);
  }, [openChats]);

  useEffect(() => {
    localStorage.setItem("minimizedChats", JSON.stringify(minimizedChats));
  }, [minimizedChats]);

  const getConversation = (
    usersKeyOrCertificationId: string,
    setConversation: (c: UserConversation | null) => void
  ) => {
    if (conversationCache[usersKeyOrCertificationId]) {
      setConversation(conversationCache[usersKeyOrCertificationId]);
      return;
    }
    AxiosHttpClient.get<UserConversation>(
      "api/v1/docuveritas/user-conversations/" + usersKeyOrCertificationId
    )
      .then((res) => {
        setConversation(res);
        setConversationCache((prev) => ({
          ...prev,
          [usersKeyOrCertificationId]: res,
        }));
      })
      .catch((error) => {
        console.error("Error fetching conversation:", error);
        setConversation(null);
        setConversationCache((prev) => ({
          ...prev,
          [usersKeyOrCertificationId]: null,
        }));
      });
  };

  const fetchMessages = async (
    usersKeyOrCertificationId: string,
    pageNumber: number = 0,
    force: boolean = false
  ) => {
    const cache = messageCache[usersKeyOrCertificationId];
    if (!force && cache && cache.page >= pageNumber) return;

    try {
      const res = await AxiosHttpClient.get<any>(
        `api/v1/docuveritas/user-conversations/messages/page`,
        {
          criteriaList: `conversation_usersKeyOrCertificationId=${usersKeyOrCertificationId}`,
          pageNumber,
          pageSize: 20,
          sort: "createdAt",
          order: "desc",
        }
      );
      setMessageCache((prev) => {
        const oldMessages = prev[usersKeyOrCertificationId]?.messages || [];
        // Si force, on remplace les messages, sinon on concatène
        return {
          ...prev,
          [usersKeyOrCertificationId]: {
            messages: force
              ? res.content.reverse()
              : [...oldMessages, ...res.content.reverse()],
            hasMore: !res.last,
            page: pageNumber,
          },
        };
      });
    } catch (err) {
      console.error("Failed to fetch messages:", err);
    }
  };

  const openChat = (id: string) => {
    setMinimizedChats((prevMinimizedChats) =>
      prevMinimizedChats.filter((chatId) => chatId !== id)
    );
    setOpenChats((prevOpenChats) => {
      if (!prevOpenChats.includes(id)) {
        const updated = [...prevOpenChats, id];
        console.log("id", id);
        console.log("openChats", updated);
        return updated;
      }
      console.log("id", id);
      console.log("openChats", prevOpenChats);
      return prevOpenChats;
    });
  };

  const minimizeChat = (id: string) => {
    setOpenChats((prevOpenChats) =>
      prevOpenChats.filter((chatId) => chatId !== id)
    );
    setMinimizedChats((prevMinimizedChats) => {
      if (!prevMinimizedChats.includes(id)) {
        return [...prevMinimizedChats, id];
      }
      return prevMinimizedChats;
    });
  };

  const closeChat = (id: string) => {
    setOpenChats(openChats.filter((chatId) => chatId !== id));
    setMinimizedChats(minimizedChats.filter((chatId) => chatId !== id));
  };

  const markAsRead = (id: string) => {
    setConversations(
      conversations.map((conv) =>
        conv.id === id ? { ...conv, unread: false } : conv
      )
    );
  };

  const sendMessage = (usersKeyOrCertificationId: string, content: string) => {
    AxiosHttpClient.post("api/v1/docuveritas/user-conversations/messages", {
      usersKeyOrCertificationId: usersKeyOrCertificationId,
      content: content,
    })
      .then(() => {})
      .catch((err) => {});
  };

  return (
    <ChatContext.Provider
      value={{
        conversations,
        openChats,
        minimizedChats,
        getConversation,
        openChat,
        minimizeChat,
        closeChat,
        markAsRead,
        sendMessage,
        conversationCache,
        messageCache,
        fetchMessages,
        setConversationCache,
        setMessageCache,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
}
